import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Vertical } from 'atoms/layout/flex';
import { HeadlineM, LabelS } from 'atoms/typography';
import { Dialog } from 'components/dialog';
import { glossary } from 'lib/glossary';

import type { PrivacyPolicyDialog_dataPartner } from './__generated__/index.graphql';
import { useUpdatePartnerOffersAgreement } from './useUpdatePartnerOffersAgreement';

const DialogContainer = styled(Vertical).attrs({ gap: 2, center: true })`
  text-align: center;
  padding: var(--unit) var(--triple-unit) var(--triple-unit) var(--triple-unit);
`;
const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  gap: var(--unit);
`;

type Props = {
  onClose: () => void;
  dataPartner: PrivacyPolicyDialog_dataPartner;
};
export const PrivacyPolicyDialog = ({ onClose, dataPartner }: Props) => {
  const { slug, name, termsUrl } = dataPartner;
  const updateAgreement = useUpdatePartnerOffersAgreement();

  const onYes = () => {
    updateAgreement({ partner: slug, value: true });
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={
        <HeadlineM as="h4" className="text-center">
          <FormattedMessage
            id="Follower.PrivacyPolicyDialog.Title"
            defaultMessage="Trusted partners"
          />
        </HeadlineM>
      }
    >
      <DialogContainer>
        <FormattedMessage
          id="Follower.PrivacyPolicyDialog.Content"
          defaultMessage="Would you like to share your information with {clubName} for marketing and promotional purposes?"
          values={{ clubName: name }}
        />
        <FlexContainer>
          <Button size="small" fullWidth color="red" onClick={onClose}>
            <FormattedMessage {...glossary.no} />
          </Button>
          <Button size="small" fullWidth color="primary" onClick={onYes}>
            <FormattedMessage {...glossary.yes} />
          </Button>
        </FlexContainer>
        <LabelS color="var(--c-nd-400)">
          <FormattedMessage
            id="Follower.PrivacyPolicyDialog.Subcontent"
            defaultMessage="Please note that you will be able to withdraw your consent to the processing of your data by {clubName} at any time. In order to do so, please refer to {clubName} privacy policy <a>here</a>."
            values={{
              clubName: name,
              termsUrl,
              a: chunks => (
                <a href={termsUrl} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ),
            }}
          />
        </LabelS>
      </DialogContainer>
    </Dialog>
  );
};

PrivacyPolicyDialog.fragments = {
  dataPartner: gql`
    fragment PrivacyPolicyDialog_dataPartner on DataPartner {
      slug
      name
      termsUrl
    }
  ` as TypedDocumentNode<PrivacyPolicyDialog_dataPartner>,
};
